import {PreventionInvalidityCode} from "../preventative-interactions";
import {buildMappedPriceLevelFieldKeys} from "../shared/price-import-helpers";
import {BaseImportTransformer, toPartNumberUpper} from "./shared";

/**
 * @param {Object} item
 * @param {Set<string>} mappedPriceLevelFieldKeys
 * @returns {Record<string, number>}
 */
function collectPricesFromRow(item, mappedPriceLevelFieldKeys) {
    const prices = {};
    Object.entries(item)
        .filter(([key]) => key.startsWith("priceLevel_") && mappedPriceLevelFieldKeys.has(key))
        .forEach(([key, value]) => {
            const levelId = key.replace("priceLevel_", "");
            let detectedPrice = value?.toString();
            detectedPrice = detectedPrice?.replaceAll("$", "")?.replaceAll(",", "")?.trim();

            if (detectedPrice?.toUpperCase() === "NULL") {
                return;
            }

            const parsedPrice = parseFloat(detectedPrice);
            if (!isNaN(parsedPrice)) {
                prices[levelId] = parsedPrice;
            }
        });
    return prices;
}

/**
 * Part price level import — part id resolution plus mapped `priceLevel_*` column parsing.
 * Follows the multi-phase contract on {@link BaseImportTransformer} (no early exit on lookup;
 * phases run so row-level codes accumulate before emit).
 * Preview (`!isFinalRun`): placeholder rows where needed + merged `preventativeIssues`.
 * Final (`isFinalRun`): `apiRequests[i]` is `null` when the row has a preventative issue or no importable prices.
 */
export class PartPriceLevelImportTransformer extends BaseImportTransformer {
    /**
     * @param {Array<Object|null|undefined>} mappedData
     * @param {boolean} isFinalRun
     * @param {{ mappingFields?: Array<{name: string}> }} [options]
     */
    async run(mappedData, isFinalRun = false, options = {}) {
        const rows = mappedData ?? [];
        const preventativeIssues = [];
        const mappedPriceLevelFieldKeys = buildMappedPriceLevelFieldKeys(options.mappingFields);
        const obsoleteRowIndices = [];

        /** @type {{index: number, item: *, partNoUpper: string, codes: string[]}[]} */
        const rowState = rows.map((item, index) => ({
            index,
            item,
            partNoUpper: toPartNumberUpper(item?.partNumber),
            codes: [],
        }));

        // ── Phase 1: preliminary (CSV-only) ─────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) continue;
            if (!rs.partNoUpper) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        // ── Phase 2: batched part lookup ────────────────────────────────────────
        const uniqueParts = [...new Set(rowState.map((r) => r.partNoUpper).filter(Boolean))].sort();
        /** @type {Record<string, *>} */
        let partIds = {};
        if (uniqueParts.length > 0) {
            const bundle = await this.ctx.fetchPartInfoByPartNumbers(uniqueParts);
            partIds = bundle.partIds;
        }

        // ── Phase 3: unknown part ───────────────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) continue;
            if (rs.partNoUpper && !partIds[rs.partNoUpper]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        // ── Emit merged issues ───────────────────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) {
                preventativeIssues.push({
                    rowIndex: rs.index,
                    originalRow: rows[rs.index],
                    invalidityCodes: [PreventionInvalidityCode.INVALID_PART],
                });
                continue;
            }
            if (!rs.codes.length) continue;
            preventativeIssues.push({
                rowIndex: rs.index,
                originalRow: rs.item,
                invalidityCodes: [...new Set(rs.codes)],
            });
        }

        const issueIndexSet = new Set(preventativeIssues.map((p) => p.rowIndex));

        const apiRequests = rows.map((item, index) => {
            if (!item) {
                return isFinalRun ? null : {partId: null, prices: {}};
            }

            const rs = rowState[index];
            if (!rs.partNoUpper) {
                return isFinalRun ? null : {partId: null, prices: {}};
            }

            const partId = partIds[rs.partNoUpper];
            const hasIssue = issueIndexSet.has(index);
            const prices = collectPricesFromRow(item, mappedPriceLevelFieldKeys);
            const hasImportablePrices = Object.keys(prices).length > 0;

            if (hasIssue) {
                return isFinalRun ? null : {partId: partId ?? null, prices: {}};
            }

            if (partId && !hasImportablePrices) {
                obsoleteRowIndices.push(index);
            }

            if (!partId || !hasImportablePrices) {
                return isFinalRun ? null : {partId: partId ?? null, prices};
            }

            return {
                partId,
                prices,
            };
        });

        return {apiRequests, preventativeIssues, obsoleteRowIndices};
    }
}
